// Constantes das tabelas SRP/CS
// Baseado nas normas ABNT NBR 14153 e ABNT NBR ISO 13849-1

export interface SRPCSTableValue {
  codigo: string;
  /** Texto em português — continua sendo o que vai para o laudo e para o banco. */
  descricao: string;
}

/** Assinatura mínima do `t` do `useI18n()` — a constante não importa React. */
type Traduzir = (chave: string) => string;

export type TipoSRPCS = 'severidade' | 'frequencia' | 'possibilidade' | 'categoria' | 'plr' | 'sil';

const RAIZ = 'apreciacaoTabelas.srpcs';

/**
 * O `codigo` (S1, F2, B, 3, d…) já é identificador estável, então ele mesmo é o
 * último segmento da chave: `apreciacaoTabelas.srpcs.severidade.S1`.
 */
export function chaveSRPCS(tipo: TipoSRPCS, codigo: string): string {
  return `${RAIZ}.${tipo}.${codigo}`;
}

/**
 * Valores permitidos para SRP/CS - Severidade da Lesão
 */
export const SRPCS_SEVERIDADE: SRPCSTableValue[] = [
  { codigo: 'S1', descricao: 'Lesão Leve (normalmente reversível)' },
  { codigo: 'S2', descricao: 'Lesão Grave (normalmente irreversível) incluindo morte' },
];

/**
 * Valores permitidos para SRP/CS - Frequência e/ou tempo de exposição ao perigo
 */
export const SRPCS_FREQUENCIA: SRPCSTableValue[] = [
  { codigo: 'F1', descricao: 'Raro a relativamente frequente e/ou baixo tempo de exposição' },
  { codigo: 'F2', descricao: 'Frequente a contínuo e/ou tempo de exposição longo' },
];

/**
 * Valores permitidos para SRP/CS - Possibilidade de evitar o perigo
 */
export const SRPCS_POSSIBILIDADE: SRPCSTableValue[] = [
  { codigo: 'P1', descricao: 'Possível sob condições específicas' },
  { codigo: 'P2', descricao: 'Quase nunca possível' },
];

/**
 * Valores permitidos para SRP/CS - Categoria de Segurança
 */
export const SRPCS_CATEGORIA: SRPCSTableValue[] = [
  { codigo: 'B', descricao: 'Categoria B - Requisitos básicos de segurança' },
  { codigo: '1', descricao: 'Categoria 1 - Requisitos básicos de segurança e componentes bem testados' },
  { codigo: '2', descricao: 'Categoria 2 - Monitoramento periódico da função de segurança' },
  { codigo: '3', descricao: 'Categoria 3 - Exigência de redundância e monitoramento' },
  { codigo: '4', descricao: 'Categoria 4 - Exigência de redundância e monitoramento contínuo' },
];

/**
 * Valores permitidos para SRP/CS - Performance Level required (PLr)
 */
export const SRPCS_PLR: SRPCSTableValue[] = [
  { codigo: 'a', descricao: 'PLr a - Baixo' },
  { codigo: 'b', descricao: 'PLr b - Baixo a médio' },
  { codigo: 'c', descricao: 'PLr c - Médio' },
  { codigo: 'd', descricao: 'PLr d - Médio a alto' },
  { codigo: 'e', descricao: 'PLr e - Alto' },
];

/**
 * Valores permitidos para SRP/CS - Safety Integrity Level (SIL)
 */
export const SRPCS_SIL: SRPCSTableValue[] = [
  { codigo: '1', descricao: 'SIL 1 - Baixo' },
  { codigo: '2', descricao: 'SIL 2 - Médio' },
  { codigo: '3', descricao: 'SIL 3 - Alto' },
  { codigo: '4', descricao: 'SIL 4 - Muito alto' },
];

/**
 * Função para obter descrição de um valor SRP/CS
 */
export function tabelaSRPCS(tipo: TipoSRPCS): SRPCSTableValue[] {
  switch (tipo) {
    case 'severidade':
      return SRPCS_SEVERIDADE;
    case 'frequencia':
      return SRPCS_FREQUENCIA;
    case 'possibilidade':
      return SRPCS_POSSIBILIDADE;
    case 'categoria':
      return SRPCS_CATEGORIA;
    case 'plr':
      return SRPCS_PLR;
    case 'sil':
      return SRPCS_SIL;
  }
}

/**
 * Mesma coisa que `obterDescricaoSRPCS`, mas no idioma da tela. Código fora da
 * tabela devolve `undefined`, igual à versão em português.
 */
export function obterDescricaoSRPCSTraduzida(
  t: Traduzir,
  tipo: TipoSRPCS,
  codigo: string
): string | undefined {
  const item = tabelaSRPCS(tipo).find((i) => i.codigo === codigo);
  return item ? t(chaveSRPCS(tipo, item.codigo)) : undefined;
}

/** `formatarValorSRPCS` no idioma da tela: `S2 - Serious injury…`. */
export function formatarValorSRPCSTraduzido(
  t: Traduzir,
  tipo: TipoSRPCS,
  codigo: string
): string {
  const descricao = obterDescricaoSRPCSTraduzida(t, tipo, codigo);
  return descricao ? `${codigo} - ${descricao}` : codigo;
}

export function obterDescricaoSRPCS(
  tipo: 'severidade' | 'frequencia' | 'possibilidade' | 'categoria' | 'plr' | 'sil',
  codigo: string
): string | undefined {
  let tabela: SRPCSTableValue[];
  switch (tipo) {
    case 'severidade':
      tabela = SRPCS_SEVERIDADE;
      break;
    case 'frequencia':
      tabela = SRPCS_FREQUENCIA;
      break;
    case 'possibilidade':
      tabela = SRPCS_POSSIBILIDADE;
      break;
    case 'categoria':
      tabela = SRPCS_CATEGORIA;
      break;
    case 'plr':
      tabela = SRPCS_PLR;
      break;
    case 'sil':
      tabela = SRPCS_SIL;
      break;
  }
  return tabela.find(item => item.codigo === codigo)?.descricao;
}

/**
 * Função para formatar valor SRP/CS com descrição
 */
export function formatarValorSRPCS(
  tipo: 'severidade' | 'frequencia' | 'possibilidade' | 'categoria' | 'plr' | 'sil',
  codigo: string
): string {
  const descricao = obterDescricaoSRPCS(tipo, codigo);
  if (descricao) {
    return `${codigo} - ${descricao}`;
  }
  return codigo;
}

/**
 * Interface para resultado da tabela de decisão SRP/CS
 */
export interface DecisaoSRPCS {
  plr: 'a' | 'b' | 'c' | 'd' | 'e';
  categoriasPossiveis: ('B' | '1' | '2' | '3' | '4')[];
  observacao?: string;
  /** Chave de dicionário do texto de `observacao`, para exibir traduzido. */
  observacaoChave?: string;
}

/**
 * Tabela de decisão SRP/CS baseada em S, F e P
 * Retorna o PLr requerido e as categorias possíveis
 */
export function calcularDecisaoSRPCS(
  severidade: 'S1' | 'S2' | undefined,
  frequencia: 'F1' | 'F2' | undefined,
  possibilidade: 'P1' | 'P2' | undefined
): DecisaoSRPCS | null {
  if (!severidade || !frequencia || !possibilidade) {
    return null;
  }

  // Tabela de decisão completa
  const tabelaDecisao: Record<string, DecisaoSRPCS> = {
    'S1-F1-P1': {
      plr: 'a',
      categoriasPossiveis: ['B', '1'],
    },
    'S1-F1-P2': {
      plr: 'b',
      categoriasPossiveis: ['B', '1', '2'],
    },
    'S1-F2-P1': {
      plr: 'b',
      categoriasPossiveis: ['B', '1', '2'],
    },
    'S1-F2-P2': {
      plr: 'c',
      categoriasPossiveis: ['1', '2', '3'],
    },
    'S2-F1-P1': {
      plr: 'c',
      categoriasPossiveis: ['2', '3'],
      observacao: 'PL c (ou d) - avaliar caso específico',
      observacaoChave: `${RAIZ}.observacao.avaliarCasoEspecifico`,
    },
    'S2-F1-P2': {
      plr: 'd',
      categoriasPossiveis: ['3'],
    },
    'S2-F2-P1': {
      plr: 'd',
      categoriasPossiveis: ['3'],
    },
    'S2-F2-P2': {
      plr: 'e',
      categoriasPossiveis: ['4'],
    },
  };

  const chave = `${severidade}-${frequencia}-${possibilidade}`;
  return tabelaDecisao[chave] || null;
}

/**
 * Tabela de decisão SIL baseada no PL (ISO 13849)
 * Retorna o SIL equivalente ao PL calculado
 */
export function calcularSILPorPL(plr: 'a' | 'b' | 'c' | 'd' | 'e' | undefined): '1' | '2' | '3' | null {
  if (!plr) {
    return null;
  }

  // Tabela de conversão PL → SIL
  const tabelaSIL: Record<'a' | 'b' | 'c' | 'd' | 'e', '1' | '2' | '3' | null> = {
    'a': null, // PL a não tem SIL equivalente
    'b': '1',
    'c': '1',
    'd': '2',
    'e': '3',
  };

  return tabelaSIL[plr] ?? null;
}

